/**
 * GuestSidebar Component
 *
 * Shows guest information in the message sidebar.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { BookingContext } from './BookingContext';

interface GuestSidebarProps {
  guest: {
    id: string;
    name: string;
    avatar: string;
    memberSince?: string;
    reviewCount?: number;
  };
  listing: {
    id: string;
    title: string;
    image: string;
  };
  booking?: {
    id: string;
    status: 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled';
    checkIn: Date;
    checkOut: Date;
    guests: number;
    totalPrice: number;
    hostPayout: number;
  };
  onAcceptBooking?: () => void;
  onDeclineBooking?: () => void;
}

export const GuestSidebar: React.FC<GuestSidebarProps> = ({
  guest,
  listing,
  booking,
  onAcceptBooking,
  onDeclineBooking,
}) => {
  return (
    <div className="w-80 border-l bg-gray-50 p-4 overflow-y-auto hidden lg:block">
      {/* Guest Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <img
            src={guest.avatar}
            alt={guest.name}
            className="w-14 h-14 rounded-full object-cover"
          />
          <div>
            <h3 className="font-semibold text-gray-900">{guest.name}</h3>
            {guest.memberSince && (
              <p className="text-sm text-gray-500">Member since {guest.memberSince}</p>
            )}
          </div>
        </div>

        {guest.reviewCount !== undefined && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <StarIcon className="w-4 h-4 text-amber-400" />
            <span>{guest.reviewCount} reviews</span>
          </div>
        )}
      </div>

      {/* Listing Info */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
        <img
          src={listing.image}
          alt={listing.title}
          className="w-full h-32 object-cover"
        />
        <div className="p-3">
          <h4 className="font-medium text-gray-900 text-sm mb-1">{listing.title}</h4>
          <Link
            to={`/host/listings/${listing.id}`}
            className="text-xs text-primary-600 hover:text-primary-700"
          >
            View listing →
          </Link>
        </div>
      </div>

      {/* Booking Context */}
      {booking && (
        <BookingContext
          booking={booking}
          onAccept={onAcceptBooking}
          onDecline={onDeclineBooking}
        />
      )}

      {/* Quick Actions */}
      <div className="mt-4 space-y-2">
        <button className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-2">
          <ArchiveIcon className="w-4 h-4" />
          Archive conversation
        </button>
        <button className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-2">
          <BellOffIcon className="w-4 h-4" />
          Mute notifications
        </button>
      </div>
    </div>
  );
};

// Icons
const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path
      fillRule="evenodd"
      d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
      clipRule="evenodd"
    />
  </svg>
);

const ArchiveIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
    />
  </svg>
);

const BellOffIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12l-9 9" />
  </svg>
);

export default GuestSidebar;
